import {
  resolveStorefrontTokens,
  STOREFRONT_RARITY_TIERS,
  STOREFRONT_THEME_PRESETS,
  STOREFRONT_TOKEN_GROUPS,
  STOREFRONT_TOKEN_KEYS,
  type StorefrontRarityKey,
  type StorefrontTokenKey,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { ChevronRight } from "lucide-react";
import { type ReactNode, useState } from "react";

import {
  countOverrides,
  setRarityOverrides,
  setTokenOverrides,
  useStorefrontConfig,
} from "../lib/storefront";
import { useTenantRole } from "../lib/useTenantRole";
import { ColorField, RadiusField } from "./StorefrontFields";
import { StorefrontPaletteGenerator } from "./StorefrontPaletteGenerator";

/** The whole `theme` column, unsaved: the four fields the contract stores, and nothing else. */
interface ThemeDraft {
  tokens: Partial<Record<StorefrontTokenKey, string>>;
  rarity: Partial<Record<StorefrontRarityKey, string>>;
  radius: string;
  fontFamily: string;
}

/**
 * The Theme tab of the Storefront page: the palette generator, then one form for the radius,
 * the font and the rarity colors, with presets and every CSS variable under Advanced. It runs
 * its own `useStorefrontConfig`, so a save from the General tab never fires its `onSaved` and
 * drops a draft in progress. A storefront section, so `StorefrontLayout` has loaded the config
 * before this mounts.
 */
export function StorefrontThemeEditor() {
  const [draft, setDraft] = useState<ThemeDraft | null>(null);
  const { query, update } = useStorefrontConfig({
    onSaved: () => setDraft(null),
  });
  const { can } = useTenantRole();

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config } = query.data;
  const theme: ThemeDraft = draft ?? {
    tokens: (config.theme.tokens ?? {}) as Partial<Record<StorefrontTokenKey, string>>,
    rarity: config.theme.rarity ?? {},
    radius: config.theme.radius ?? "",
    fontFamily: config.theme.fontFamily ?? "",
  };

  const setToken = (key: StorefrontTokenKey, value: string) =>
    setDraft({ ...theme, tokens: { ...theme.tokens, [key]: value } });

  // One write path for both save buttons: the generator hands over a full
  // palette, the form its draft. Rarity, radius and font ride along from the
  // page either way, so saving a palette never discards an unsaved corner radius.
  const saveTheme = (tokens: Partial<Record<StorefrontTokenKey, string>>) =>
    update.mutate({
      theme: {
        // Empty means "unset" — drop the key so it falls through to the default palette.
        tokens: setTokenOverrides(tokens),
        rarity: setRarityOverrides(theme.rarity),
        radius: theme.radius.trim() || undefined,
        fontFamily: theme.fontFamily.trim() || undefined,
      },
    });

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    saveTheme(theme.tokens);
  };

  const overridden = countOverrides(config.theme.tokens);

  return (
    <div className="flex flex-col divide-y">
      <p className="pb-6 text-sm text-muted-foreground">
        Pick a primary, a page background and optionally a secondary; every other color is derived
        from them, with text chosen for contrast. Review what changes, then save. Radius, font and
        rarity colors below; presets and individual CSS variables under Advanced.
      </p>

      <div className="py-6">
        <StorefrontPaletteGenerator
          saved={resolveStorefrontTokens(config.theme.tokens)}
          radius={theme.radius.trim() || undefined}
          fontFamily={theme.fontFamily.trim() || undefined}
          canEdit={can("editor")}
          saving={update.isPending}
          onSave={saveTheme}
        />
      </div>

      <form className="flex flex-col divide-y" onSubmit={save}>
        <Row title="Shape and type" description="Corner rounding and the storefront's font stack.">
          <div className="grid gap-5 sm:grid-cols-2">
            <RadiusField
              value={theme.radius}
              onChange={(radius) => setDraft({ ...theme, radius })}
            />
            <div className="grid content-start gap-2">
              <Label htmlFor="theme-font-family">Font family</Label>
              <Input
                id="theme-font-family"
                type="text"
                value={theme.fontFamily}
                onChange={(e) => setDraft({ ...theme, fontFamily: e.target.value })}
                placeholder="Inter, sans-serif"
              />
            </div>
          </div>
        </Row>

        <Row
          title="Rarity colors"
          description="Tier colors for item cards and the opener's spinner rail. Each field shows the stock rail color until you override it — clear one to go back."
        >
          <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
            {STOREFRONT_RARITY_TIERS.map((tier) => (
              <ColorField
                key={tier.key}
                id={`theme-rarity-${tier.key}`}
                label={tier.label}
                hint={`rarity ${tier.key}`}
                fallback={tier.default}
                value={theme.rarity[tier.key] ?? ""}
                onChange={(value) =>
                  setDraft({ ...theme, rarity: { ...theme.rarity, [tier.key]: value } })
                }
              />
            ))}
          </div>
        </Row>

        <Advanced
          summary={
            overridden === 0
              ? "Every variable on the default palette"
              : `${overridden} of ${STOREFRONT_TOKEN_KEYS.length} variables overridden`
          }
        >
          <Row
            title="Presets"
            description="Fills every color field below with a ready-made palette. Nothing is stored until you save, so you can adjust individual colors first."
          >
            <div className="flex flex-col gap-1">
              {STOREFRONT_THEME_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setDraft({ ...theme, tokens: { ...preset.tokens } })}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span className="flex shrink-0 -space-x-1">
                    {(["background", "card", "primary"] as const).map((token) => (
                      <span
                        key={token}
                        className="size-4 rounded-full ring-1 ring-border"
                        style={{ backgroundColor: preset.tokens[token] }}
                      />
                    ))}
                  </span>
                  <span className="font-medium">{preset.name}</span>
                  <span className="text-muted-foreground">{preset.description}</span>
                </button>
              ))}
            </div>
          </Row>

          {STOREFRONT_TOKEN_GROUPS.map((group) => (
            <Row key={group.label} title={group.label} description={group.description}>
              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                {group.tokens.map((token) => (
                  <ColorField
                    key={token.key}
                    id={`theme-token-${token.key}`}
                    label={token.label}
                    hint={`--${token.key}`}
                    fallback={token.default}
                    value={theme.tokens[token.key] ?? ""}
                    onChange={(value) => setToken(token.key, value)}
                  />
                ))}
              </div>
            </Row>
          ))}
        </Advanced>

        <div className="pt-6">
          <Button type="submit" size="sm" disabled={update.isPending || !can("editor")}>
            {update.isPending ? "Saving…" : "Save theme"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Label column on the left, controls on the right; a hairline between rows. */
function Row({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-x-10 gap-y-3 py-6 md:grid-cols-[11rem_minmax(0,1fr)]">
      <div className="grid content-start gap-1">
        <h2 className="text-sm font-medium">{title}</h2>
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * Presets and the per-variable editor, folded away by default. The generator
 * above is the intended way to build a theme; this is where staff go to nudge
 * one variable after the fact, or to read what a saved theme actually sets.
 */
function Advanced({ summary, children }: { summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="theme-advanced"
        className="-mx-2 my-3 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
      >
        <ChevronRight
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${
            open ? "rotate-90" : ""
          }`}
          aria-hidden
        />
        <span className="font-medium">Advanced</span>
        <span className="text-muted-foreground">
          Presets and every CSS variable, one field each · {summary}
        </span>
      </button>
      {open && (
        <div id="theme-advanced" className="flex flex-col divide-y border-t">
          {children}
        </div>
      )}
    </div>
  );
}
